import type { FullStatsDto } from '@poker-stats/contracts';
import formatHands from '../../utils/formatHands';
import formatWinnings from '../../utils/formatWinnings';
import { buildPortableUltraStats, type PortableUltraStatEntry } from './postflopBuilders';
import { customStatKey, type UltraPrefs } from './ultraPrefs';

type PreflopStatKey =
  | 'threebet'
  | 'foldedtothreebetpreflop'
  | 'squeeze'
  | 'raisedthreebetpreflop'
  | 'foldedtofourbetpreflop'
  | 'raisedfourbetpreflop'
  | 'sawshowdown'
  | 'wonwhensawflop'
  | 'wonshowdown';

const preflopOverview: Array<[string, PreflopStatKey]> = [
  ['3bet', 'threebet'],
  ['F3bet', 'foldedtothreebetpreflop'],
  ['SQZ', 'squeeze'],
  ['4bet', 'raisedthreebetpreflop'],
  ['F4bet', 'foldedtofourbetpreflop'],
  ['5bet', 'raisedfourbetpreflop'],
  ['WTSD', 'sawshowdown'],
  ['WWSF', 'wonwhensawflop'],
  ['W$SD', 'wonshowdown'],
];

export const DEFAULT_ULTRA_SLOT_LABELS = [
  'VPIP',
  'PFR',
  'Hands',
  'W',
  'WR',
  '3bet',
  'F3bet',
  'SQZ',
  '4bet',
  'F4bet',
  '5bet',
  'WTSD',
  'WWSF',
  'W$SD',
] as const;

export function buildOverviewUltraEntries(
  stats: Partial<FullStatsDto> | null | undefined,
): PortableUltraStatEntry[] {
  const safeStats = stats ?? {};
  return [
    { label: 'VPIP', value: safeStats.vpip ?? '—' },
    { label: 'PFR', value: safeStats.pfr ?? '—' },
    { label: 'Hands', value: safeStats.hands == null ? '—' : formatHands(safeStats.hands, 1) },
    {
      label: 'W',
      value: safeStats.winnings == null ? '—' : formatWinnings(safeStats.winnings, 1, true),
    },
    { label: 'WR', value: safeStats.bb100 ?? '—' },
    ...preflopOverview.map(([label, key]) => ({
      label,
      value: safeStats[key] ?? '—',
    })),
  ];
}

export type UltraCatalogEntry = PortableUltraStatEntry & {
  key: string;
};

export function buildUltraStatCatalogEntries(
  stats: Partial<FullStatsDto> | null | undefined,
  prefs: UltraPrefs,
): UltraCatalogEntry[] {
  const byKey = new Map<string, UltraCatalogEntry>();

  for (const entry of buildOverviewUltraEntries(stats)) {
    byKey.set(entry.label, { ...entry, key: entry.label });
  }

  for (const entry of buildPortableUltraStats(stats ?? {})) {
    if (!byKey.has(entry.label)) {
      byKey.set(entry.label, { ...entry, key: entry.label });
    }
  }

  for (const custom of prefs.customStats) {
    const label = custom.label.trim();
    if (!label) {
      continue;
    }

    const key = customStatKey(custom.id);
    byKey.set(key, {
      key,
      label,
      value: custom.value,
    });
  }

  return Array.from(byKey.values()).sort((left, right) =>
    left.label.localeCompare(right.label, 'ru'),
  );
}
